// CreditPoolWidget — live Apollo export-credit pool, shown in the GTM header
// beside the Pause-all switch.
//
// For united_ai admins the chip is also an editor: clicking opens a small popover
// to SET the balance (match Apollo remaining) or ADD purchased credits, POSTing to
// /api/gtm/credit-pool. The credit_pool SSE then ticks the chip live, so there is
// no manual refresh. Non-admins see a read-only chip.
//
// T5 OPERATOR-ONLY. This is the raw internal credit pool, which per the locked
// pricing decision customers never see. Do not mount it on any customer-facing
// surface.
//
// Styling mirrors the OutboundPage Badge/pill idiom (MUI sx + THEME tokens). A
// dropped stream dims the last known numbers rather than blanking, so a reconnect
// never reads as "0 credits".

import React, { useState } from 'react';
import { Box, Typography, Popover, TextField, Button, CircularProgress } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { THEME } from './genesisData';
import useCreditPoolStream from '../../hooks/useCreditPoolStream';
import { setCreditPool, useGtmAdmin } from '../../hooks/useGtm';
import PoolPill from './PoolPill';

const LOW_FRACTION = 0.15; // amber below 15% of ceiling

const fmt = (n) =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString('en-US');

const CreditPoolWidget = () => {
  const { available, ceiling, consumed, reserved, status } = useCreditPoolStream();
  const isAdmin = useGtmAdmin();

  const [anchorEl, setAnchorEl] = useState(null);
  const [mode, setMode] = useState('set'); // 'set' | 'add'
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  const isDegraded = status === 'error' || status === 'reconnecting';
  const hasNumbers = available != null && ceiling != null;
  const fraction = hasNumbers && ceiling > 0 ? available / ceiling : null;

  // Red on error or a genuinely empty pool; amber when low; neutral otherwise.
  let tone = 'neutral';
  if (status === 'error' || available === 0) tone = 'red';
  else if (fraction != null && fraction < LOW_FRACTION) tone = 'amber';

  // Ceiling + reset info for the tooltip. There is no auto-reset date; the ceiling
  // is the manually-set monthly quota.
  const detail = `Ceiling (monthly quota) ${fmt(ceiling)}. Consumed ${fmt(consumed)}, reserved ${fmt(reserved)}. The ceiling is set manually; there is no automatic reset.`;
  const tip = isAdmin
    ? `Click to update Apollo credit balance. ${detail} Internal only; customers never see this.`
    : status === 'error'
    ? `Credit feed unreachable, showing the last known figure. ${detail} Internal only.`
    : status === 'reconnecting'
    ? `Reconnecting to the credit feed, showing the last known figure. ${detail}`
    : `Apollo export credits remaining. ${detail} Internal only; customers never see this.`;

  const openEditor = (e) => {
    if (!isAdmin) return;
    setMode('set');
    setValue(available != null ? String(available) : '');
    setEditError(null);
    setAnchorEl(e.currentTarget);
  };
  const closeEditor = () => {
    if (submitting) return;
    setAnchorEl(null);
    setEditError(null);
  };
  const switchMode = (m) => {
    setMode(m);
    setValue(m === 'set' ? (available != null ? String(available) : '') : '');
    setEditError(null);
  };

  const submit = async () => {
    const n = Number(String(value).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n) || n < 0) {
      setEditError('Enter a non-negative number.');
      return;
    }
    if (mode === 'add' && available == null) {
      setEditError('Waiting for the current balance to load.');
      return;
    }
    // ADD posts the new absolute balance (current live value + entered).
    const target = mode === 'add' ? (Number(available) || 0) + n : n;
    setSubmitting(true);
    setEditError(null);
    try {
      await setCreditPool(target);
      // The credit_pool SSE tick reconciles the chip to the true value; just close.
      setAnchorEl(null);
    } catch (err) {
      // Rule 3: surface inline, keep the popover open, never a silent success.
      setEditError((err && err.message) || 'Could not update the credit pool.');
    } finally {
      setSubmitting(false);
    }
  };

  const modeBtnSx = (active) => ({
    flex: 1,
    cursor: 'pointer',
    textAlign: 'center',
    py: '5px',
    fontSize: '12px',
    fontWeight: 700,
    borderRadius: '6px',
    border: `1px solid ${active ? THEME.mint : THEME.border}`,
    color: active ? THEME.mint : THEME.textSecondary,
    background: active ? THEME.mintFaint : 'transparent',
    userSelect: 'none',
  });

  const previewAdd =
    mode === 'add' && available != null ? (Number(available) || 0) + (Number(String(value).replace(/[^0-9.]/g, '')) || 0) : null;

  return (
    <>
      {/* Single remaining number ('198 credits'); ceiling + reset live in the tooltip.
          Rendered via the shared PoolPill so the Instantly quota pill can reuse it. */}
      <PoolPill
        label="Apollo"
        value={fmt(available)}
        unit="credits"
        tone={tone}
        tooltip={tip}
        live={status === 'live'}
        clickable={isAdmin}
        onClick={openEditor}
        dim={isDegraded}
        trailing={
          isAdmin ? (
            <EditOutlinedIcon
              className="pool-pill-trailing"
              sx={{ fontSize: 13, color: THEME.textMuted, flexShrink: 0, ml: '1px', transition: 'color 120ms' }}
            />
          ) : null
        }
      />

      {isAdmin && (
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={closeEditor}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              background: THEME.card,
              color: THEME.textPrimary,
              border: `1px solid ${THEME.borderStrong}`,
              borderRadius: '10px',
              p: 2,
              width: 300,
              fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
            },
          }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary, mb: 1.25 }}>
            Apollo credit pool
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Box sx={modeBtnSx(mode === 'set')} onClick={() => switchMode('set')}>
              Set
            </Box>
            <Box sx={modeBtnSx(mode === 'add')} onClick={() => switchMode('add')}>
              Add
            </Box>
          </Box>

          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
            {mode === 'set' ? 'Set balance to (match Apollo)' : 'Add purchased credits'}
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={mode === 'set' ? 'e.g. 3750' : 'e.g. 100'}
            disabled={submitting}
            InputProps={{ sx: { fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } } }}
          />

          {previewAdd != null && (
            <Typography sx={{ fontSize: '11px', color: THEME.mint, fontWeight: 700, mt: 0.75 }}>
              New balance: {fmt(previewAdd)}
            </Typography>
          )}

          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5, mt: 1.25 }}>
            Apollo does not expose the balance to us, so update this whenever you buy credits or want it to match Apollo exactly.
          </Typography>

          {editError && (
            <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{editError}</Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={submitting}
              onClick={closeEditor}
              sx={{ textTransform: 'none', fontSize: '12px', color: THEME.textSecondary, borderColor: THEME.borderStrong }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={submitting}
              onClick={submit}
              startIcon={submitting ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
              sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 700, background: THEME.mint, color: '#06281f', boxShadow: 'none', '&:hover': { background: '#0fa884', boxShadow: 'none' } }}
            >
              {submitting ? 'Updating…' : 'Update'}
            </Button>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default CreditPoolWidget;
